import { AuthState } from "@okta/okta-auth-js";
import { Formik } from "formik";
import { useEffect, useRef } from "react";
import dataConstants from "../../constants/dataConstants";
import toastMessages from "../../services/utils/helpers/toastMessage";
import MultipleFileUploader from "../global/MultipleFileUploader";
import AddDocumentsPayload from "../../services/models/API/requestModels/addDocuments.model";
import useAddDocuments from "../../services/utils/custom hooks/Documents/useAddDocuments";
import Button from "../organism/Button";
import validations from "../../services/validations";
import { normalizeUploadFile } from "../../services/utils/helpers/fileNameNormalizer";

function TransitDocuments({
  values,
  setFieldValue,
  setFieldTouched,
}: {
  readonly values: AddDocumentsPayload;
  readonly setFieldValue?: any;
  readonly setFieldTouched?: any;
}) {
  const inputRef = useRef<any>();

  function handleRemove(fileIndex: number) {
    return () => {
      setFieldValue(
        "transitDocuments",
        values.transitDocuments.filter((document, index) => index !== fileIndex)
      );
    };
  }

  const validateFile = (file: any): boolean => {
    let sizeInMB = null;
    sizeInMB = file.size / 1024 / 1024;

    if (values.transitDocuments.length > 0) {
      if (
        values.transitDocuments.some((item: any) => item.name === file.name)
      ) {
        alert("File already added");
        return false;
      }
    }

    function getExt(filename: string): any {
      const ext = filename.split(".").pop();
      if (ext === filename) return "";
      return ext;
    }

    if (!dataConstants.validTypes.includes(getExt(file.name)?.toLowerCase())) {
      toastMessages.error(`Invalid file format`);
      return false;
    }

    if (sizeInMB > 55) {
      alert("File size limit is being exceeded");
      return false;
    }

    return true;
  };

  function handleFiles(event: any): void {
    let inputFiles = event.target.files;
    let temp: any[] = values.transitDocuments;

    for (const element of inputFiles) {
      const normalizedFile = normalizeUploadFile(element);
      if (validateFile(normalizedFile)) {
        temp.push(normalizedFile);
      }
    }

    setFieldValue("transitDocuments", temp);
  }

  async function handleDrop(event: any) {
    event.preventDefault();
    event.stopPropagation();
    let inputFiles = event.dataTransfer.files;
    let temp: any[] = [];

    if (values.transitDocuments.length > 0) {
      temp = [...values.transitDocuments];
    }

    for (const element of inputFiles) {
      const normalizedFile = normalizeUploadFile(element);
      if (validateFile(normalizedFile)) {
        temp.push(normalizedFile);
      }
    }

    setFieldValue("transitDocuments", temp);
    setFieldTouched("transitDocuments", true);
  }

  return (
    <div>
      <MultipleFileUploader
        handleDrop={handleDrop}
        handleRemove={handleRemove}
        files={values.transitDocuments}
        inputRef={inputRef}
        customText="Please upload any additional file(s) required for transmission to Clinical Services. Accepted formats include .pdf, .png, .jpeg, and .doc/*, with a maximum file size of 50 MB."
        buttonText="Choose or drop file"
        hideUploaderText
        uploadButtonContainerClassName="max-w-[300px]"
      />
      <input
        type="file"
        id="file"
        multiple
        accept=".png, .jpeg, .pdf, .doc/*"
        ref={inputRef}
        onChange={handleFiles}
        style={{ display: "none" }}
      />
    </div>
  );
}

function UploadDocumentsPopup({
  authState,
  orderId,
  closePopup,
}: {
  authState: AuthState | null;
  orderId: string;
  closePopup: Function;
}) {
  const { mutate, isPending, isSuccess } = useAddDocuments(authState);
  const initialValues: AddDocumentsPayload = {
    orderId,
    transitDocuments: [],
  };

  useEffect(() => {
    if (isSuccess) {
      closePopup();
    }
  }, [isSuccess]);

  return (
    <div className="max-w-[80vw] max-h-[50vh] !overflow-auto">
      <Formik
        isInitialValid={false}
        initialValues={initialValues}
        validationSchema={validations.addDocumentsValidation}
        onSubmit={(values) => mutate(values)}
      >
        {({ values, handleSubmit, setFieldValue }) => {
          return (
            <form
              onSubmit={handleSubmit}
              className={"flex flex-col justify-end items-end"}
            >
              <TransitDocuments values={values} setFieldValue={setFieldValue} />
              <Button
                textInformation={{
                  text: "Transmit file(s)",
                  fontWeight: "font-semibold",
                }}
                buttonTheme="primary"
                loading={isPending}
                type="submit"
                disabled={isPending || values.transitDocuments.length === 0}
                onClick={() => {}}
                className="my-5"
              />
            </form>
          );
        }}
      </Formik>
    </div>
  );
}

export default UploadDocumentsPopup;
